import { useCallback, useEffect, useState } from 'react';
import { CheckCircleFilled, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Pagination,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { fileUrl, post } from '../../api';

const PAGE_SIZE = 20;
const GRID_PAGE_SIZE = 9;
const MAX_SELECTION = 500;

const STATUS = {
  0: { text: '进行中', color: 'blue' },
  1: { text: '已完成', color: 'green' },
  2: { text: '已取消', color: 'red' },
};

function formatTime(value) {
  return value ? value.replace('T', ' ').slice(0, 19) : '-';
}

function formatRange(start, end) {
  if (!start || !end) {
    return '-';
  }
  return `${formatTime(start)} ~ ${formatTime(end)}`;
}

/** 虚拟用户选择弹窗 */
function UserPicker({ open, value, onCancel, onOk }) {
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelected(Object.fromEntries((value || []).map((item) => [item.id, item])));
    setKeyword('');
    setPage(1);
  }, [open, value]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const rsp = await post('/admin/user/list', {
        isVirtual: 1,
        keyword,
        page,
        size: 10,
      });
      if (rsp.code) {
        message.error(rsp.msg || '加载失败');
        return;
      }
      setList(rsp.data.list || []);
      setTotal(rsp.data.total || 0);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setLoading(false);
    }
  }, [keyword, page]);

  useEffect(() => {
    if (open) {
      fetchList();
    }
  }, [open, fetchList]);

  const selectedRows = Object.values(selected);

  const pick = (record, checked) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (checked) {
        next[record.id] = record;
      } else {
        delete next[record.id];
      }
      return next;
    });
  };

  const pickPage = (checked, rows) => {
    setSelected((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        if (checked) {
          next[row.id] = row;
        } else {
          delete next[row.id];
        }
      });
      return next;
    });
  };

  const columns = [
    { title: '邮箱', dataIndex: 'email', render: (v) => v || '-' },
    { title: '昵称', dataIndex: 'nickname', render: (v) => v || '-' },
    { title: '手机号', dataIndex: 'mobile', render: (v) => v || '-' },
    { title: '注册时间', dataIndex: 'createTime', render: formatTime },
  ];

  return (
    <Modal
      title="选择虚拟用户"
      open={open}
      width={820}
      onCancel={onCancel}
      onOk={() => {
        if (selectedRows.length > MAX_SELECTION) {
          message.error(`一次最多选择 ${MAX_SELECTION} 个虚拟用户`);
          return;
        }
        onOk(selectedRows);
      }}
      okText={`确定（已选 ${selectedRows.length}）`}
    >
      <Space style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="搜索邮箱 / 昵称 / 手机号"
          style={{ width: 320 }}
          onSearch={(val) => {
            setKeyword(val.trim());
            setPage(1);
          }}
        />
        <span style={{ color: '#888' }}>不选则默认使用全部虚拟用户</span>
      </Space>
      <Table
        rowKey="id"
        size="small"
        bordered
        loading={loading}
        columns={columns}
        dataSource={list}
        pagination={{
          current: page,
          pageSize: 10,
          total,
          showSizeChanger: false,
          showTotal: (count) => `共 ${count} 条`,
          onChange: setPage,
        }}
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys: Object.keys(selected),
          onSelect: pick,
          onSelectAll: pickPage,
        }}
      />
    </Modal>
  );
}

export default function VirtualOrders() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [storeOptions, setStoreOptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [gridList, setGridList] = useState([]);
  const [gridTotal, setGridTotal] = useState(0);
  const [gridPage, setGridPage] = useState(1);
  const [gridKeyword, setGridKeyword] = useState('');
  const [gridLoading, setGridLoading] = useState(false);
  const [form] = Form.useForm();

  const storeId = Form.useWatch('storeId', form);
  const useAllProducts = Form.useWatch('useAllProducts', form);
  const useAllUsers = Form.useWatch('useAllUsers', form);
  const range = Form.useWatch('range', form);
  const targetCount = Form.useWatch('targetCount', form);
  const selectedStore = storeOptions.find((item) => item.value === storeId);

  const fetchList = useCallback(async (current) => {
    setLoading(true);
    try {
      const rsp = await post('/admin/virtual-order/list', { page: current, size: PAGE_SIZE });
      if (rsp.code) {
        message.error(rsp.msg || '加载失败');
        return;
      }
      setList(rsp.data.list || []);
      setTotal(rsp.data.total || 0);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList(page);
  }, [page, fetchList]);

  // 有进行中的任务时每 10 秒刷新一次进度
  useEffect(() => {
    if (!list.some((item) => item.status === 0)) {
      return undefined;
    }
    const timer = setInterval(() => fetchList(page), 10000);
    return () => clearInterval(timer);
  }, [list, page, fetchList]);

  const fetchProducts = useCallback(async () => {
    if (!storeId) {
      return;
    }
    setGridLoading(true);
    try {
      const rsp = await post('/admin/virtual-order/products', {
        storeId,
        keyword: gridKeyword,
        page: gridPage,
        size: GRID_PAGE_SIZE,
      });
      if (rsp.code) {
        message.error(rsp.msg || '加载失败');
        return;
      }
      setGridList(rsp.data.list || []);
      setGridTotal(rsp.data.total || 0);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setGridLoading(false);
    }
  }, [storeId, gridKeyword, gridPage]);

  useEffect(() => {
    if (createOpen) {
      fetchProducts();
    }
  }, [createOpen, fetchProducts]);

  const searchStores = async (keyword) => {
    try {
      const rsp = await post('/admin/store/list', { keyword: keyword.trim(), page: 1, size: 20 });
      if (rsp.code) {
        message.error(rsp.msg || '加载失败');
        return;
      }
      setStoreOptions(
        (rsp.data.list || []).map((item) => ({
          value: item.id,
          label: `${item.name}（…${item.id.slice(-6)}）`,
          name: item.name,
          logo: item.logo,
          email: item.email,
        })),
      );
    } catch (e) {
      message.error('网络异常，请稍后再试');
    }
  };

  const openCreate = () => {
    form.resetFields();
    setProducts([]);
    setUsers([]);
    setGridKeyword('');
    setGridPage(1);
    setGridList([]);
    setGridTotal(0);
    setCreateOpen(true);
    searchStores('');
  };

  const toggleProduct = (record) => {
    if (useAllProducts !== 0) {
      return;
    }
    setProducts((prev) => {
      if (prev.some((item) => item.id === record.id)) {
        return prev.filter((item) => item.id !== record.id);
      }
      if (prev.length >= MAX_SELECTION) {
        message.warning(`一次最多选择 ${MAX_SELECTION} 个商品`);
        return prev;
      }
      return [...prev, record];
    });
  };

  /** 勾选整页：已选列表与当前页求并集或差集 */
  const togglePage = (checked) => {
    if (useAllProducts !== 0) {
      return;
    }
    setProducts((prev) => {
      if (!checked) {
        return prev.filter((item) => !gridList.some((pageItem) => pageItem.id === item.id));
      }
      const merged = new Map(prev.map((item) => [item.id, item]));
      gridList.forEach((item) => merged.set(item.id, item));
      const next = [...merged.values()];
      if (next.length > MAX_SELECTION) {
        message.warning(`一次最多选择 ${MAX_SELECTION} 个商品，已截断`);
        return next.slice(0, MAX_SELECTION);
      }
      return next;
    });
  };

  const submit = async () => {
    const values = await form.validateFields();
    if (values.useAllProducts === 0 && products.length === 0) {
      message.error('请选择参与下单的商品');
      return;
    }
    if (values.useAllUsers === 0 && users.length === 0) {
      message.error('请选择参与下单的虚拟用户');
      return;
    }
    setSubmitting(true);
    try {
      const rsp = await post('/admin/virtual-order/create', {
        storeId: values.storeId,
        useAllProducts: values.useAllProducts,
        productIds: values.useAllProducts === 0 ? products.map((item) => item.id) : [],
        useAllUsers: values.useAllUsers,
        userIds: values.useAllUsers === 0 ? users.map((item) => item.id) : [],
        startTime: values.range[0].format('YYYY-MM-DD HH:mm:ss'),
        endTime: values.range[1].format('YYYY-MM-DD HH:mm:ss'),
        targetCount: values.targetCount,
      });
      if (rsp.code) {
        message.error(rsp.msg || '创建失败');
        return;
      }
      message.success('任务已创建，系统会按下单时间范围陆续生成订单');
      setCreateOpen(false);
      setPage(1);
      fetchList(1);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelTask = async (record) => {
    try {
      const rsp = await post('/admin/virtual-order/cancel', { id: record.id });
      if (rsp.code) {
        message.error(rsp.msg || '取消失败');
        return;
      }
      message.success('任务已取消，已生成的订单保留');
      fetchList(page);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    }
  };

  const intervalTip = (() => {
    if (!range || !range[0] || !range[1] || !targetCount) {
      return null;
    }
    const minutes = range[1].diff(range[0], 'minute') / targetCount;
    if (!Number.isFinite(minutes) || minutes <= 0) {
      return null;
    }
    return `平均约 ${minutes >= 60 ? `${(minutes / 60).toFixed(1)} 小时` : `${Math.round(minutes)} 分钟`} 一单`;
  })();

  const columns = [
    { title: '店铺', dataIndex: 'storeName' },
    {
      title: '商品范围',
      dataIndex: 'useAllProducts',
      render: (value, record) => (value === 1 ? '全部在售商品' : `${record.productCount} 个指定商品`),
    },
    {
      title: '下单时间范围',
      render: (_, record) => formatRange(record.startTime, record.endTime),
    },
    {
      title: '虚拟用户',
      dataIndex: 'useAllUsers',
      render: (value, record) => (value === 1 ? '全部虚拟用户' : `${record.userCount} 个指定用户`),
    },
    {
      title: '进度',
      render: (_, record) => (
        <span>
          {record.doneCount} / {record.targetCount}
        </span>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (value) => <Tag color={STATUS[value]?.color}>{STATUS[value]?.text || value}</Tag>,
    },
    { title: '创建时间', dataIndex: 'createTime', render: formatTime },
    {
      title: '操作',
      width: 110,
      render: (_, record) =>
        record.status === 0 ? (
          <Popconfirm
            title="取消该任务？"
            description="已生成的订单会保留，剩余订单不再生成"
            onConfirm={() => cancelTask(record)}
          >
            <Button size="small" danger>
              取消
            </Button>
          </Popconfirm>
        ) : (
          '-'
        ),
    },
  ];

  const selectedIds = new Set(products.map((item) => item.id));
  const pageAllSelected = gridList.length > 0 && gridList.every((item) => selectedIds.has(item.id));
  const pagePartSelected = !pageAllSelected && gridList.some((item) => selectedIds.has(item.id));

  return (
    <div>
      <div className="list-toolbar">
        <div className="list-title">虚拟下单</div>
        <div className="list-actions">
          <Button icon={<ReloadOutlined />} onClick={() => fetchList(page)}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新建任务
          </Button>
        </div>
      </div>

      <Table
        rowKey="id"
        bordered
        columns={columns}
        dataSource={list}
        loading={loading}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          showTotal: (count) => `共 ${count} 条`,
          onChange: setPage,
        }}
      />

      <Modal
        title={<span className="modal-title-bar">虚拟任务</span>}
        open={createOpen}
        width={1080}
        style={{ top: 24 }}
        styles={{ body: { padding: 16 } }}
        okText="创建任务"
        confirmLoading={submitting}
        onCancel={() => setCreateOpen(false)}
        onOk={submit}
      >
        <div className="vo-layout">
          <div className="vo-left">
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                useAllProducts: 1,
                useAllUsers: 1,
                targetCount: 20,
                range: [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')],
              }}
            >
              <Form.Item
                name="storeId"
                label="店铺"
                rules={[{ required: true, message: '请选择店铺' }]}
                extra={
                  selectedStore ? (
                    <span className="vo-store-id">
                      ID {selectedStore.value} · {selectedStore.email || '无登录邮箱'}
                    </span>
                  ) : null
                }
              >
                <Select
                  showSearch
                  filterOption={false}
                  placeholder="输入店铺名称或 ID 搜索"
                  options={storeOptions}
                  onSearch={searchStores}
                  listItemHeight={52}
                  optionRender={(option) => (
                    <div className="vo-store-option">
                      <span className="vo-store-logo">
                        {option.data.logo ? (
                          <img src={fileUrl(option.data.logo, 480)} alt="" />
                        ) : (
                          (option.data.name || 'S').slice(0, 1)
                        )}
                      </span>
                      <span className="vo-store-main">
                        <span className="vo-store-name">{option.data.name}</span>
                        <span className="vo-store-full-id">{option.data.value}</span>
                      </span>
                    </div>
                  )}
                  onChange={() => {
                    setProducts([]);
                    setGridKeyword('');
                    setGridPage(1);
                  }}
                />
              </Form.Item>

              <Form.Item name="useAllProducts" label="商品范围" required>
                <Radio.Group>
                  <Radio value={1}>全部在售商品</Radio>
                  <Radio value={0}>指定商品</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name="range"
                label="下单时间范围"
                rules={[{ required: true, message: '请选择下单时间范围' }]}
                extra={
                  <span style={{ display: 'inline-block', marginTop: 6 }}>
                    💡 订单会在这个范围里均匀随机分布，不会出现急促连续下单、也不会阻塞导致功能异常
                  </span>
                }
              >
                <DatePicker.RangePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="targetCount"
                label="下单数量"
                rules={[{ required: true, message: '请输入下单数量' }]}
                extra={
                  intervalTip ? (
                    <span style={{ display: 'inline-block', marginTop: 6 }}>
                      💡 按当前设置，{intervalTip}
                    </span>
                  ) : null
                }
              >
                <InputNumber min={1} max={5000} style={{ width: 200 }} />
              </Form.Item>

              <Form.Item name="useAllUsers" label="虚拟用户" required>
                <Radio.Group>
                  <Radio value={1}>全部虚拟用户</Radio>
                  <Radio value={0}>指定虚拟用户</Radio>
                </Radio.Group>
              </Form.Item>

              {useAllUsers === 0 && (
                <Form.Item>
                  <Space wrap>
                    <Button size="small" onClick={() => setUserPickerOpen(true)}>
                      {users.length ? `已选 ${users.length} 个用户` : '选择虚拟用户'}
                    </Button>
                    {users.slice(0, 2).map((item) => (
                      <Tag key={item.id}>{item.nickname || item.email || item.id}</Tag>
                    ))}
                    {users.length > 2 && <Tag>等 {users.length} 个</Tag>}
                  </Space>
                </Form.Item>
              )}

              <Alert
                type="info"
                showIcon
                message="生成的订单为已付款（待发货）状态，按下单时间回填到订单记录；时间范围已过去的会立即补齐历史订单。"
              />
            </Form>
          </div>

          <div className="vo-right">
            <div className="vo-right-head">
              <Space size={8}>
                <Checkbox
                  title="全选本页"
                  checked={pageAllSelected}
                  indeterminate={pagePartSelected}
                  disabled={useAllProducts !== 0 || gridList.length === 0}
                  onChange={(e) => togglePage(e.target.checked)}
                />
                <span>已选 {products.length} 个商品</span>
                {useAllProducts === 0 && products.length > 0 && (
                  <Button size="small" type="link" onClick={() => setProducts([])}>
                    清空
                  </Button>
                )}
              </Space>
              <Space>
                <Input.Search
                  size="small"
                  allowClear
                  placeholder="搜索商品标题"
                  style={{ width: 200 }}
                  disabled={!storeId}
                  onSearch={(val) => {
                    setGridKeyword(val.trim());
                    setGridPage(1);
                  }}
                />
              </Space>
            </div>

            {!storeId ? (
              <div className="vo-empty">请先选择店铺</div>
            ) : gridLoading && gridList.length === 0 ? (
              <div className="vo-empty">加载中...</div>
            ) : gridList.length === 0 ? (
              <div className="vo-empty">该店铺没有在售商品</div>
            ) : (
              <>
                <div className="vo-grid">
                  {gridList.map((item) => {
                    const active = selectedIds.has(item.id);
                    return (
                      <div
                        key={item.id}
                        className={`vo-card${active ? ' vo-card-active' : ''}${
                          useAllProducts !== 0 ? ' vo-card-disabled' : ''
                        }`}
                        onClick={() => toggleProduct(item)}
                        title={item.title}
                      >
                        <div className="vo-card-img">
                          <img src={fileUrl(item.image, 480)} alt="" />
                        </div>
                        <div className="vo-card-title">{item.title || item.productId}</div>
                        <div className="vo-card-price">${item.sellingPrice}</div>
                        {active && <CheckCircleFilled className="vo-card-check" />}
                      </div>
                    );
                  })}
                </div>
                <div className="vo-pager">
                  <Pagination
                    size="small"
                    current={gridPage}
                    pageSize={GRID_PAGE_SIZE}
                    total={gridTotal}
                    showSizeChanger={false}
                    showTotal={(count) => `共 ${count} 个在售商品`}
                    onChange={setGridPage}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>

      <UserPicker
        open={userPickerOpen}
        value={users}
        onCancel={() => setUserPickerOpen(false)}
        onOk={(rows) => {
          setUsers(rows);
          setUserPickerOpen(false);
        }}
      />
    </div>
  );
}
